import React, { useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@strata-monorepo/shared-ui/components/ui/dialog'
import energyBillOverlay from '../images/energy_bill_example.png'
import energyBillMobile from '../images/energy_bill_example_mobile.png'

import { cn } from '@strata-monorepo/shared-ui/utils/cn'

interface EnergyBillHelpModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  isMobile?: boolean
}
export function EnergyBillHelpModal({
  open,
  onOpenChange,
  isMobile,
}: EnergyBillHelpModalProps): React.ReactElement {
  const legendCircleRef = useRef<HTMLDivElement>(null)
  const titleId = React.useId()
  const descriptionId = React.useId()

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className='strata:max-w-[908px] strata:max-h-[760px] strata:overflow-y-auto strata:gap-2 strata:p-4 strata:@md:p-8'
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <DialogHeader>
          <DialogTitle
            id={titleId}
            className='strata:font-bold strata:text-left strata:text-sm strata:@md:text-base'
          >
            Understand your energy bill
          </DialogTitle>
          <DialogDescription
            id={descriptionId}
            className='strata:text-xs sm:strata:text-sm strata:text-gray-700 strata:text-xs strata:@md:text-sm strata:text-left'
          >
            These areas show where to find your service address, billing period, total kWh usage,
            plan name, and bill amount. Make sure to check both pages, some invoice details may be
            on the <span className='strata:font-semibold'>second page</span> of your bill.
          </DialogDescription>
        </DialogHeader>
        <div className='strata:space-y-4 strata:@md:space-y-6'>
          {/* Legend */}
          <div
            className={cn(
              'strata:flex strata:flex-wrap strata:text-xs',
              isMobile
                ? 'strata:gap-x-4 strata:gap-y-3 strata:justify-start'
                : 'strata:gap-2 strata:@sm:gap-3 strata:justify-center strata:@sm:justify-start strata:@md:justify-center',
            )}
          >
            <div
              className={cn(
                'strata:flex strata:items-center strata:gap-2',
                isMobile && 'strata:basis-[30%]',
              )}
            >
              <div
                ref={legendCircleRef}
                className={cn(
                  'strata:flex strata:items-center strata:justify-center strata:rounded-full strata:bg-system-warning-accent strata:text-system-warning-foreground strata:font-bold strata:shrink-0',
                  isMobile
                    ? 'strata:text-xs strata:w-[17px] strata:h-[17px]'
                    : 'strata:text-sm strata:@sm:w-5 strata:@sm:h-5',
                )}
              >
                1
              </div>
              <span className='strata:font-bold strata:text-xs strata:@md:text-sm'>Address</span>
            </div>
            <div
              className={cn(
                'strata:flex strata:items-center strata:gap-2',
                isMobile && 'strata:basis-[30%]',
              )}
            >
              <div
                className={cn(
                  'strata:flex strata:items-center strata:justify-center strata:rounded-full strata:bg-system-warning-accent strata:text-system-warning-foreground strata:font-bold strata:shrink-0',
                  isMobile
                    ? 'strata:text-xs strata:w-[17px] strata:h-[17px]'
                    : 'strata:text-sm strata:w-5 strata:h-5',
                )}
              >
                2
              </div>
              <span className='strata:font-bold strata:text-xs strata:@md:text-sm'>Date</span>
            </div>
            <div
              className={cn(
                'strata:flex strata:items-center strata:gap-2',
                isMobile && 'strata:basis-[30%]',
              )}
            >
              <div
                className={cn(
                  'strata:flex strata:items-center strata:justify-center strata:rounded-full strata:bg-system-warning-accent strata:text-system-warning-foreground strata:font-bold strata:shrink-0',
                  isMobile
                    ? 'strata:text-xs strata:w-[17px] strata:h-[17px]'
                    : 'strata:text-sm strata:w-5 strata:h-5',
                )}
              >
                3
              </div>
              <span className='strata:font-bold strata:text-xs strata:@md:text-sm'>
                Usage (kWh)
              </span>
            </div>
            <div
              className={cn(
                'strata:flex strata:items-center strata:gap-2',
                isMobile && 'strata:basis-[30%]',
              )}
            >
              <div
                className={cn(
                  'strata:flex strata:items-center strata:justify-center strata:rounded-full strata:bg-system-warning-accent strata:text-system-warning-foreground strata:font-bold strata:shrink-0',
                  isMobile
                    ? 'strata:text-xs strata:w-[17px] strata:h-[17px]'
                    : 'strata:text-sm strata:w-5 strata:h-5',
                )}
              >
                4
              </div>
              <span className='strata:font-bold strata:text-xs strata:@md:text-sm'>Plan name</span>
            </div>
            <div
              className={cn(
                'strata:flex strata:items-center strata:gap-2',
                isMobile && 'strata:basis-[30%]',
              )}
            >
              <div
                className={cn(
                  'strata:flex strata:items-center strata:justify-center strata:rounded-full strata:bg-system-warning-accent strata:text-system-warning-foreground strata:font-bold strata:shrink-0',
                  isMobile
                    ? 'strata:text-xs strata:w-[17px] strata:h-[17px]'
                    : 'strata:text-sm strata:w-5 strata:h-5',
                )}
              >
                5
              </div>
              <span className='strata:font-bold strata:text-xs strata:@md:text-sm'>
                Bill amount
              </span>
            </div>
          </div>
          {/* Bill Image with Overlays */}
          <div className='strata:relative strata:rounded-lg strata:overflow-hidden strata:w-auto strata:h-auto strata:-mx-4 strata:@qm/qc:mx-auto'>
            <img
              src={isMobile ? energyBillMobile : energyBillOverlay}
              alt='Sample energy bill showing where to find service address, billing period, usage, plan name, and bill amount'
              className='strata:w-full strata:h-auto strata:block'
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
